import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, LoginAs } from '../../contexts/AuthContext';
import { Button, Input, Typography, Row, Col } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { UserOutlined, LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import '../../styles/colors.css';

const { Title, Text } = Typography;

const copy: Record<
  LoginAs,
  { head: string; blurb: string; registerPath: string; registerLabel: string }
> = {
  Mentee: {
    head: 'Mentee Girişi',
    blurb: 'Mentorluk, bootcamp ve kariyer fırsatları',
    registerPath: '/register-mentee',
    registerLabel: 'Mentee kayıt',
  },
  Mentor: {
    head: 'Mentor Girişi',
    blurb: 'Dersler, görüşmeler ve danışmanlık paneli',
    registerPath: '/register-mentor',
    registerLabel: 'Mentor kayıt',
  },
  Company: {
    head: 'Kurumsal Giriş',
    blurb: 'İş ilanı, aday arama, başvurular',
    registerPath: '/register-company',
    registerLabel: 'Kurumsal kayıt',
  },
  Admin: {
    head: 'Yönetici Girişi',
    blurb: 'Sistem yönetim paneli',
    registerPath: '/login',
    registerLabel: 'Giriş seçeneklerine dön',
  },
};

type Props = { as: LoginAs };

const RoleLogin: React.FC<Props> = ({ as }) => {
  const RESEND_COOLDOWN_SECONDS = 10 * 60;
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const meta = copy[as];
  const showResend = as !== 'Admin';

  const getCooldownStorageKey = (emailValue: string) =>
    `resendVerificationCooldownUntil:${emailValue.trim().toLowerCase()}`;

  const getRemainingFromStorage = (emailValue: string) => {
    const key = getCooldownStorageKey(emailValue);
    const until = Number(localStorage.getItem(key) || 0);
    if (!until) return 0;
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  };

  const startCooldown = (seconds: number, emailValue: string) => {
    if (!emailValue) return;
    const normalizedEmail = emailValue.trim().toLowerCase();
    localStorage.setItem(
      getCooldownStorageKey(normalizedEmail),
      String(Date.now() + seconds * 1000)
    );
    setResendCountdown(seconds);
  };

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (loginError) {
      const timer = setTimeout(() => setLoginError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [loginError]);

  useEffect(() => {
    if (!email) {
      setResendCountdown(0);
      return;
    }
    setResendCountdown(getRemainingFromStorage(email));
  }, [email]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const leftPanelFeatures = useMemo((): { title: string; items: string[] } => {
    switch (as) {
      case 'Mentee':
        return {
          title: 'Mentee panelinde neler var?',
          items: [
            'Profil, eğitim, tecrübe ve sertifika bilgilerinizi düzenleme',
            'Bootcamp ve bire bir görüşmelere başvurma',
            'İş ilanlarına başvuru, referans ve favori isteklerini izleme',
            'Mentorlarla eşleşme ve randevu süreçlerini yönetme',
          ],
        };
      case 'Mentor':
        return {
          title: 'Mentor panelinde neler var?',
          items: [
            'Bire bir ve bootcamp seanslarını, takviminizi yönetme',
            'Musaitlik, hizmet paketleri ve fiyatlandırma ayarları',
            'Aday/mentee görüşmeleri ve toplantı planlama',
            'İçerik, reklam alanları ve abonelik tercihlerinize erişim',
          ],
        };
      case 'Company':
        return {
          title: 'Kurumsal panelde neler var?',
          items: [
            'İş ilanı oluşturma, güncelleme ve yayın yönetimi',
            'Aday arama, aday havuzu ve favori aday listeleri',
            'Başvuruları inceleme, mülakat ve aday iletişimi',
            'Şirket profili, abonelik ve izlenme bilgileri',
          ],
        };
      case 'Admin':
        return {
          title: 'Yönetici paneli',
          items: [
            'Kullanıcı, kurum ve onay akışı yönetimi',
            'Kategori, içerik ve sistem operasyonu',
            'Abonelik ve fiyatlandırma görünürlüğü (yetkiye göre)',
          ],
        };
      default:
        return { title: '', items: [] };
    }
  }, [as]);

  const validateForm = () => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');
    setLoginError('');
    setShowResendVerification(false);

    if (!email) {
      setEmailError('E-posta adresinizi girin!');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Geçerli bir e-posta adresi girin!');
      isValid = false;
    }
    if (!password) {
      setPasswordError('Şifrenizi girin!');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalı!');
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await login(email, password, as);
      setShowResendVerification(false);
      toast.success('Giriş başarılı!');
      const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;
      if (userRole === 'Admin') {
        navigate('/admin/panel');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Giriş başarısız! Lütfen bilgilerinizi kontrol edin.';
      const shouldShowResend = showResend
        && (errorMessage.toLowerCase().includes('henüz aktif değil') ||
        errorMessage.toLowerCase().includes('doğrulama mailini onaylayın'));
      setLoginError(errorMessage);
      setShowResendVerification(shouldShowResend);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }
    setResendLoading(true);
    try {
      const response = await apiService.resendVerificationEmail(email);
      if (response.success) {
        startCooldown(RESEND_COOLDOWN_SECONDS, email);
        toast.success(response.message || 'Doğrulama maili tekrar gönderildi.');
      } else {
        toast.error(response.message || 'Doğrulama maili gönderilemedi.');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Doğrulama maili gönderilemedi.';
      const minuteMatch = errorMessage.match(/(\d+)\s*dakika/i);
      if (minuteMatch?.[1]) {
        const remainingSeconds = Number(minuteMatch[1]) * 60;
        if (remainingSeconds > 0) {
          startCooldown(remainingSeconds, email);
        }
      }
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', display: 'flex' }}>
        <Row style={{ width: '100%', minHeight: '100vh', margin: 0 }}>
          <Col
            xs={0}
            lg={12}
            className="auth-split-brand"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
          >
            <div style={{ position: 'absolute', top: 24, left: 24, zIndex: 3 }}>
              <Link to="/login" className="auth-back-link">
                <ArrowLeftOutlined /> Tüm giriş seçenekleri
              </Link>
            </div>
            <div style={{ textAlign: 'center', color: 'white', maxWidth: 440, padding: '28px 24px', zIndex: 2 }}>
              <Title level={1} style={{ color: 'white', marginBottom: 4, letterSpacing: '-0.03em', fontWeight: 800 }}>Menteem</Title>
              <Title level={2} style={{ color: 'white', fontSize: 26, marginTop: 8, fontWeight: 600 }}>{meta.head}</Title>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15 }}>{meta.blurb}</Text>
              <div className="auth-feature-panel">
                <Text
                  style={{
                    color: 'rgba(255,255,255,0.96)',
                    fontSize: 15,
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: 12,
                  }}
                >
                  {leftPanelFeatures.title}
                </Text>
                {leftPanelFeatures.items.map((t) => (
                  <div key={t} className="auth-feature-line">
                    • {t}
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={24} lg={12} className="auth-split-form-column">
            <div className="auth-mobile-brand">
              <div>
                <Text strong style={{ color: '#fff', fontSize: 17, display: 'block', lineHeight: 1.3 }}>Menteem</Text>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>{meta.head}</Text>
              </div>
              <Link to="/login" className="auth-back-link auth-back-link--compact">
                <ArrowLeftOutlined /> Seçenekler
              </Link>
            </div>
            <div className="auth-form-card" style={{ width: '100%', maxWidth: 400 }}>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <Title level={2} style={{ marginBottom: 4, color: 'var(--text-primary)' }}>Hoş Geldiniz</Title>
                {as !== 'Admin' && (
                  <Text type="secondary" style={{ fontSize: 13 }}>Aşağıdaki e-posta ve şifre bu hesap türüne ait olmalıdır.</Text>
                )}
              </div>
              <div style={{ marginBottom: 20 }}>
                <Text strong>E-posta</Text>
                <Input
                  prefix={<UserOutlined style={{ color: 'var(--primary-600)' }} />}
                  placeholder="E-posta"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="large"
                  style={{ marginTop: 8, borderRadius: 12, height: 50 }}
                />
                {emailError && <Text type="danger" style={{ fontSize: 12 }}>{emailError}</Text>}
              </div>
              <div style={{ marginBottom: 20 }}>
                <Text strong>Şifre</Text>
                <Input.Password
                  prefix={<LockOutlined style={{ color: 'var(--primary-600)' }} />}
                  placeholder="Şifre"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="large"
                  style={{ marginTop: 8, borderRadius: 12, height: 50 }}
                />
                {passwordError && <Text type="danger" style={{ fontSize: 12 }}>{passwordError}</Text>}
              </div>
              <Button type="primary" block size="large" loading={loading} onClick={handleSubmit} style={{ height: 50, fontWeight: 600 }}>
                {loading ? 'Giriş...' : 'Giriş Yap'}
              </Button>
              {loginError && (
                <div className="auth-alert-error" role="alert">
                  {loginError}
                </div>
              )}
              {showResend && showResendVerification && (
                <Button
                  type="default"
                  block
                  style={{ marginTop: 10 }}
                  onClick={handleResendVerification}
                  disabled={resendLoading || resendCountdown > 0}
                >
                  {resendLoading ? 'Gönderiliyor...' : resendCountdown > 0 ? `Tekrar gönder (${formatCountdown(resendCountdown)})` : 'Doğrulama mailini tekrar gönder'}
                </Button>
              )}
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                {as === 'Admin' ? (
                  <Link to={meta.registerPath} style={{ fontWeight: 600 }}>{meta.registerLabel}</Link>
                ) : (
                  <Text>
                    Hesabınız yok mu? <Link to={meta.registerPath} style={{ fontWeight: 600 }}>{meta.registerLabel}</Link>
                  </Text>
                )}
              </div>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Link to="/forgot-password" style={{ color: 'var(--text-tertiary)' }}>Şifremi Unuttum</Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </form>
  );
};

export default RoleLogin;
